<script setup lang="ts">
import {
  autoPlacement,
  autoUpdate,
  offset,
  useFloating,
  type Placement,
  type Strategy,
} from "@floating-ui/vue";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

import { type ButtonProps } from "@/components/ui/SimpleButton.vue";

interface DropdownMenuProps extends ButtonProps {
  allowedPlacements?: Placement[];
  strategy?: Strategy;
  highlightWhenOpen?: boolean;
  helpLabel?: string;
  isInline?: boolean;
}

defineOptions({
  inheritAttrs: false,
});

const {
  label = null,
  iconLeft = null,
  iconRight = null,
  kind = "ghost",
  allowedPlacements = ["top-end", "bottom-end", "top-start", "bottom-start"],
  strategy = "fixed",
  highlightWhenOpen = false,
  helpLabel = "",
  isInline = false,
} = defineProps<DropdownMenuProps>();
const emit = defineEmits(["open", "close"]);

const isOpen = ref(false);
const el = ref<HTMLDivElement>();
const reference = ref<HTMLElement>();
const floating = ref<HTMLDivElement>();
const { floatingStyles } = useFloating(reference, floating, {
  strategy,
  middleware: [autoPlacement({ allowedPlacements: allowedPlacements as Placement[] }), offset(3)],
  whileElementsMounted: autoUpdate,
});

const isNested = computed(() => {
  // check if the dropdown is nested in another dropdown
  let parent = el.value?.parentElement;
  while (parent) {
    if (parent.classList.contains("dropdown")) {
      return true;
    }
    parent = parent.parentElement;
  }
  return false;
});

function onClick(e: Event) {
  if (el.value && floating.value) {
    // is it a click outside or a click on a final item ?
    const t = e.target as HTMLElement;
    const isTargetInside = el.value.contains(t);
    const isTargetFinal = t.closest('[data-is-final="true"]') !== null;

    if (!isTargetInside || isTargetFinal) {
      close();
    }
  }
}

// Mouse move listener to close the dropdown when the mouse moves
function onMouseMove() {
  if (
    el.value &&
    !el.value.checkVisibility({
      opacityProperty: true,
      contentVisibilityAuto: true,
      visibilityProperty: true,
    })
  ) {
    close();
  }
}

function toggle() {
  if (isOpen.value) {
    close();
  } else {
    open();
  }
}

function open() {
  isOpen.value = true;
  emit("open");
}

function close() {
  isOpen.value = false;
  emit("close");
}

// Intersection observer to close the dropdown when it is not visible
const intersectionObserver = new IntersectionObserver(
  ([entry]) => {
    if (entry && !entry.isIntersecting) {
      close();
    }
  },
  {
    root: document.body,
    threshold: 0,
  }
);

const transitionName = computed(() => {
  const isVisible = el.value?.checkVisibility({
    opacityProperty: true,
    contentVisibilityAuto: true,
    visibilityProperty: true,
  });
  return isVisible ? "fade" : "";
});

onMounted(() => {
  document.addEventListener("click", onClick);
  document.addEventListener("mousemove", onMouseMove);
  if (el.value) {
    intersectionObserver.observe(el.value);
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("click", onClick);
  document.removeEventListener("mousemove", onMouseMove);
  intersectionObserver.disconnect();
});

defineExpose({
  toggle,
  open,
  close,
});
</script>

<template>
  <div
    class="dropdown"
    :class="{ 'open-highlight': isOpen && highlightWhenOpen, nested: isNested, inline: isInline }"
    ref="el"
  >
    <button
      class="skore-button main-button"
      :class="[kind]"
      @click="toggle()"
      ref="reference"
      v-bind="$attrs"
    >
      <i v-if="iconLeft !== null" :class="[iconLeft]" class="icon left" />
      <span v-if="helpLabel.length > 0" class="help-label">
        {{ helpLabel }}
      </span>
      <span v-if="label" class="label">{{ label }}</span>
      <i v-if="iconRight !== null" :class="[iconRight]" class="icon right" />
    </button>
    <Transition :name="transitionName">
      <div class="items shadow-md" v-if="isOpen" ref="floating" :style="floatingStyles">
        <slot></slot>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.dropdown {
  position: relative;
  display: inline-block;

  &.nested .main-button {
    display: flex;
    min-width: 100%;
    justify-content: flex-start;
    border: none;
    border-radius: 0;
    background-color: transparent;
  }

  &.inline .main-button {
    max-height: 100%;
    border: none;
    background-color: transparent;
  }

  &.nested .main-button:hover {
    background-color: var(--background-secondary-hover);
  }

  &.inline .main-button:hover {
    background-color: transparent;
  }

  .items {
    position: fixed;
    z-index: 9000;
    display: flex;
    overflow: visible;
    width: max-content;
    flex-direction: column;
    border: solid var(--stroke-width-md) var(--border-tertiary);
    border-radius: var(--numbers-8);
    background-color: var(--background-primary);

    > *,
    :deep(.dropdown-item) {
      border-bottom: solid var(--stroke-width-md) var(--border-tertiary);

      &:first-child {
        overflow: hidden;
        border-top-left-radius: var(--numbers-8);
        border-top-right-radius: var(--numbers-8);
      }

      &:last-child {
        overflow: hidden;
        border-bottom: none;
        border-bottom-left-radius: var(--numbers-8);
        border-bottom-right-radius: var(--numbers-8);
      }
    }
  }

  .help-label {
    color: var(--content-disabled);
  }
}
</style>
